import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Sidebar } from "./components/Layout";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const CustomersPage = lazy(() => import("./pages/CustomersPage"));
const CoreLoadersPage = lazy(() => import("./pages/CoreLoadersPage"));
const WholeSellersPage = lazy(() => import("./pages/WholeSellersPage"));
const ConsignmentsPage = lazy(() => import("./pages/ConsignmentsPage"));
const PaymentsPage = lazy(() => import("./pages/PaymentsPage"));
const ExpensesPage = lazy(() => import("./pages/ExpensesPage"));
const StaffPage = lazy(() => import("./pages/StaffPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const RatesPage = lazy(() => import("./pages/RatesPage"));
const LedgersPage = lazy(() => import("./pages/LedgersPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const InvoicesPage = lazy(() => import("./pages/InvoicesPage"));
const VendorPortalPage = lazy(() => import("./pages/VendorPortalPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));

const PageSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Admin Layout with Sidebar
const AdminLayout = () => {
  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="main-content flex-1">
        <Outlet />
      </div>
    </div>
  );
};

// Vendor Partner Route Guard
const VendorPartnerRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== "vendor_partner" && user.role !== "core_loader") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Admin Route Guard
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === "vendor_partner" || user.role === "core_loader") {
    return <Navigate to="/vendor-portal" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<PageSpinner />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />

          {/* Vendor Partner Portal */}
          <Route
            path="/vendor-portal"
            element={
              <VendorPartnerRoute>
                <VendorPortalPage />
              </VendorPartnerRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            element={
              <AdminRoute>
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              </AdminRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/core-loaders" element={<CoreLoadersPage />} />
            <Route path="/whole-sellers" element={<WholeSellersPage />} />
            <Route path="/vendor-partners" element={<CoreLoadersPage />} />
            <Route path="/vendors" element={<WholeSellersPage />} />
            <Route path="/consignments" element={<ConsignmentsPage />} />
            <Route path="/payments" element={<PaymentsPage />} />
            <Route path="/invoices" element={<InvoicesPage />} />
            <Route path="/expenses" element={<ExpensesPage />} />
            <Route path="/staff" element={<StaffPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/rates" element={<RatesPage />} />
            <Route path="/ledgers" element={<LedgersPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          {/* Default Redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
